// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseDebouncedSearchOptions {
  /** How long after the last keystroke the term is committed. */
  delay?: number;
  /** How long before the "Searching…" affordance appears. */
  indicatorDelay?: number;
}

export interface DebouncedSearch {
  /** What the user has typed so far — bind this to the text field. */
  input: string;
  setInput: (value: string) => void;
  /** The COMMITTED term — put this, not `input`, in the request params. */
  search: string;
  /** True between `indicatorDelay` and the commit; drives the inline hint. */
  isSearching: boolean;
  /** Empty the box (the caller re-focuses it if it wants to). */
  clear: () => void;
}

/**
 * Debounced free-text search for a list page, paired with the page reset a
 * new search requires.
 *
 * Searching from page 3 against a narrower result set lands on an empty table,
 * so committing a term must also send the list back to page 1. That reset is
 * the reason this is a shared hook rather than three lines in each page: the
 * obvious way to write it — listing `setPage` in the effect's dependency array
 * — is an INFINITE LOOP. `setPage` writes the URL, `useTableQuery` rebuilds its
 * callbacks from the new `searchParams`, `setPage`'s identity changes, the
 * effect re-runs, restarts the timer and commits again. The `setPage` ref below
 * is what breaks that cycle, and it exists once here instead of once per page.
 *
 * Search text is deliberately NOT part of the URL (unlike sort, facets and
 * page): it is debounced local input, and round-tripping every committed term
 * through history would fight the same reset machinery. Documented as a
 * deliberate asymmetry rather than an oversight.
 */
export function useDebouncedSearch(
  setPage: (page: number) => void,
  { delay = 500, indicatorDelay = 300 }: UseDebouncedSearchOptions = {},
): DebouncedSearch {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Skip the mount pass: `input` and `search` already agree, and firing the
  // page reset on mount would clobber a `?page=` the visitor arrived with.
  const isFirstRender = useRef(true);

  // See the loop explanation above.
  const setPageRef = useRef(setPage);
  useEffect(() => { setPageRef.current = setPage; }, [setPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Delayed so the hint doesn't flicker on every keystroke.
    const indicatorTimer = setTimeout(() => setIsSearching(true), indicatorDelay);

    const commitTimer = setTimeout(() => {
      setSearch(input.trim());
      setPageRef.current(1);
      setIsSearching(false);
    }, delay);

    return () => {
      clearTimeout(indicatorTimer);
      clearTimeout(commitTimer);
    };
  }, [input, delay, indicatorDelay]);

  const clear = useCallback(() => setInput(""), []);

  return { input, setInput, search, isSearching, clear };
}
